import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';

import { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import { saveAs } from 'file-saver';

import { useResizeObserver } from 'usehooks-ts';

import { useAppDispatch, useAppSelector } from '@store/hooks';

import { Table, Button, Input, DatePicker, Tooltip, Form, Switch, Flex, Select, notification, Tag, InputNumber } from 'antd';
import { ColumnsType } from 'antd/es/table';

import {
    LoadingOutlined,
    ReloadOutlined,
    CheckCircleFilled,
    UnorderedListOutlined,
    FileExcelOutlined,
    CheckOutlined,
    WarningOutlined,
    FormOutlined,
    CheckCircleOutlined,
    EyeFilled,
} from '@ant-design/icons';

import ConsignmentCard from '@components/consignment-card/consignment-card';

import FormHeader from '@controls/form-header/form-header';
import Filter from '@controls/filter/filter';
import Toolbar from '@controls/toolbar/toolbar';
import TariffCard from '@controls/tariff-card/tariff-card';
import Bill from '@controls/bill/bill';
import Comment from '@controls/comment/comment';

import { serverFetch } from '@src/server';

import { exception } from '@extensions/notification';
import { toFinanceString, dataURItoBlob, getEnumList, getDate } from '@extensions/utils';
import { userLoaded, setFilter } from '@store/actions';

import { IBillFilter } from '@entities/bill-filter';
import { IBill } from '@entities/bill';
import { IUser } from '@entities/user';
import { ICity } from '@entities/city';
import { ITruck } from '@entities/truck';
import { IItem } from '@entities/item';
import { IConsigneeTariffSetting } from '@entities/consignee-tariff-setting';
import { ICountry } from '@entities/country';
import { ICargoCarrier } from '@entities/cargo-carrier';

import { UnitType } from '@enums/unit-type';
import { WeightType } from '@enums/weight-type';
import { Currency, enumSign as currencySign } from '@enums/currency';
import { BillStatus, enumLabel as billStatusLabel } from '@enums/bill-status';
import { UserType } from '@enums/user-type';
import { IEnumItem } from '@enums/enum-item';

import { AirplaneIcon, PenIcon } from '@icons/index';

dayjs.extend(utc);

interface IBillHeader {
    billDate?: Dayjs;
    totalPrice: number;
    totalPriceFinal: number;
    totalPriceRub: number;
    totalPriceFinalRub: number;
    bills: Array<IBill>;
}

const filterContext: string = 'BillHeaders';

const BillHeaders = () => {
    const { userId } = useParams();

    const initFilter: IBillFilter = {
        userId: userId,
        statuses: [BillStatus.ToVerify, BillStatus.Completed],
    };

    const containerRef = useRef<HTMLDivElement>(null);
    const { width = 0, height = 0 } = useResizeObserver({
        ref: containerRef,
        box: 'border-box',
    });

    const filterRef = useRef<HTMLDivElement>(null);
    const { width: filterWidth = 0, height: filterHeight = 0 } = useResizeObserver({
        ref: filterRef,
        box: 'border-box',
    });
    const d = useAppDispatch();

    const { RangePicker } = DatePicker;

    const filter = useAppSelector<IBillFilter>((s) => s.filters[filterContext]);

    const [api, notificationContextHolder] = notification.useNotification();

    const [showFilter, setShowFilter] = useState<boolean>(true);

    const [headers, setHeaders] = useState<Array<IBillHeader>>([]);
    const [selectedIds, setSelectedIds] = useState<React.Key[]>([]);
    const [refreshRequired, setRefreshRequired] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [openBill, setOpenBill] = useState<boolean>(false);
    const [bills, setBills] = useState<Array<IBill>>([]);
    const [currentBill, setCurrentBill] = useState<IBill>();
    const [user, setUser] = useState<IUser>();
    const [cities, setCities] = useState<Array<ICity>>([]);
    const [tariffSettings, setTariffSettings] = useState<Array<IConsigneeTariffSetting>>([]);
    const [trucks, setTrucks] = useState<Array<ITruck>>([]);
    const [contractors, setContractors] = useState<Array<IUser>>([]);
    const [countries, setCountries] = useState<Array<ICountry>>([]);
    const [airCargoCarriers, setAirCargoCarriers] = useState<Array<ICargoCarrier>>([]);
    const [items, setItems] = useState<Array<IItem>>([]);
    const [statuses] = useState<Array<IEnumItem>>(getEnumList(BillStatus, billStatusLabel));

    useEffect(() => {
        let cleanup = false;

        const fetchData = async () => {
            const promises = [
                await serverFetch(`users/info/${userId}`, { method: 'GET' })
                    .then((data) => {
                        return data;
                    })
                    .catch((ex) => {
                        exception(api, 'Ошибка получения пользователя', ex, () => d(userLoaded(undefined)));
                    }),
                await serverFetch('cities', { method: 'GET' })
                    .then((data) => {
                        return data;
                    })
                    .catch((ex) => {
                        exception(api, 'Ошибка получения городов', ex, () => d(userLoaded(undefined)));
                    }),
                await serverFetch(`consignees/tariffsettings`, { method: 'GET' })
                    .then((data) => {
                        return data;
                    })
                    .catch((ex) => {
                        exception(api, 'Ошибка получения настроек тарифа грузополучателей', ex, () => d(userLoaded(undefined)));
                    }),
                await serverFetch('bills/trucks', { method: 'GET' })
                    .then((data) => {
                        return data;
                    })
                    .catch((ex) => {
                        exception(api, 'Ошибка получения машин', ex, () => d(userLoaded(undefined)));
                    }),
                await serverFetch('users', { method: 'GET', queryParams: { types: UserType.Supplier, isActiveOnly: true } })
                    .then((data) => {
                        return data;
                    })
                    .catch((ex) => {
                        exception(api, 'Ошибка получения поставщиков', ex, () => d(userLoaded(undefined)));
                    }),
                await serverFetch('countries', { method: 'GET', queryParams: { withTransportOnly: true } })
                    .then((data) => {
                        return data;
                    })
                    .catch((ex) => {
                        exception(api, 'Ошибка получения стран', ex, () => d(userLoaded(undefined)));
                    }),

                await serverFetch('items', { method: 'GET' })
                    .then((data) => {
                        return data;
                    })
                    .catch((ex) => {
                        exception(api, 'Ошибка получения продуктов', ex, () => d(userLoaded(undefined)));
                    }),
                await serverFetch('cargocarriers/air', { method: 'GET' })
                    .then((data) => {
                        return data;
                    })
                    .catch((ex) => {
                        exception(api, 'Ошибка получения авиаперевозчиков', ex, () => d(userLoaded(undefined)));
                    }),
            ];

            Promise.all([promises]).then((result) => {
                if (cleanup) return;

                setUser(result[0][0] || []);
                setCities(result[0][1] || []);
                setTariffSettings(result[0][2] || []);
                setTrucks(
                    (result[0][3] || []).sort((a: ITruck, b: ITruck) => (dayjs(a.departureOn).isAfter(dayjs(b.departureOn)) ? -1 : 1))
                );
                setContractors(result[0][4] || []);
                setCountries(result[0][5].filter((c: ICountry) => c.generateTariff));
                setItems(result[0][6] || []);
                setAirCargoCarriers(result[0][7]);
            });
        };

        fetchData();

        return () => {
            cleanup = true;
        };
    }, []);

    useEffect(() => {
        let cleanup = false;

        if (!refreshRequired) return;

        setSelectedIds([]);
        setHeaders([]);

        const fetchData = async () => {
            setLoading(true);

            const promises = [
                await serverFetch('bills/headers', { method: 'GET', queryParams: filter })
                    .then((data) => {
                        return data;
                    })
                    .catch((ex) => {
                        exception(api, 'Ошибка получения счетов', ex, () => d(userLoaded(undefined)));
                    }),
            ];

            Promise.all([promises]).then((result) => {
                if (cleanup) return;

                setHeaders(result[0][0] || []);
                setLoading(false);
                setRefreshRequired(false);
            });
        };

        fetchData();

        return () => {
            cleanup = true;
        };
    }, [refreshRequired]);

    useEffect(() => {
        if (!filter || filter.userId != userId) {
            d(setFilter(initFilter, filterContext));
            return;
        }

        //        delayAction(() => setRefreshRequired(true));
    }, [filter]);

    useEffect(() => {
        if (bills && bills.length > 0) {
            setOpenBill(true);
        }
    }, [bills]);

    const onSelectChange = (selectedRowKeys: React.Key[], selectedRows: Array<IBill>) => {
        if (selectedRows.length == 1) {
            const bill: IBill = selectedRows[0];

            if (bill.status != BillStatus.Abandoned) {
                setCurrentBill(bill);
            }
        } else {
            setCurrentBill(undefined);
        }

        const selectedBills = selectedRows.filter((r) => r.status != BillStatus.Abandoned);
        const ids = selectedBills.map((b) => b.id);
        setSelectedIds(ids);
    };

    const onExport = () => {
        serverFetch('bills/export', { method: 'GET', queryParams: filter })
            .then((data) => {
                const fileBody = `data:${data.contentType};base64,${data.fileContents}`;
                saveAs(dataURItoBlob(fileBody), data.fileDownloadName);
            })
            .catch((ex) => {
                exception(api, 'Ошибка выгрузки счетов', ex, () => d(userLoaded(undefined)));
            });
    };

    const renderFilter = () => {
        return (
            <div ref={filterRef}>
                <Filter
                    display={showFilter}
                    items={[
                        <Input
                            style={{ width: 130 }}
                            key='number'
                            placeholder='Номер счета'
                            value={filter?.number}
                            onChange={(data) => {
                                d(setFilter({ ...filter, number: data.target.value }, filterContext));
                            }}
                        />,
                        <Select
                            key='status'
                            placeholder='Статус'
                            value={filter?.statuses}
                            size='middle'
                            mode='multiple'
                            maxTagCount='responsive'
                            allowClear
                            style={{ width: 250 }}
                            onChange={(value) => d(setFilter({ ...filter, statuses: value }, filterContext))}
                            options={statuses.map((s) => {
                                return { value: s.value, label: s.label };
                            })}
                        />,
                        <InputNumber
                            style={{ width: 130 }}
                            key='consigneeCode'
                            placeholder='ID клиента'
                            value={filter?.consigneeCode}
                            onChange={(value) => {
                                d(setFilter({ ...filter, consigneeCode: value }, filterContext));
                            }}
                        />,
                        <Input
                            style={{ width: 130 }}
                            key='markingCode'
                            placeholder='Маркировка'
                            value={filter?.markingCode}
                            onChange={(data) => {
                                d(setFilter({ ...filter, markingCode: data.target.value }, filterContext));
                            }}
                        />,
                        <Input
                            style={{ width: 230 }}
                            key='legalName'
                            placeholder='Контрагент'
                            value={filter?.legalName}
                            onChange={(data) => {
                                d(setFilter({ ...filter, legalName: data.target.value }, filterContext));
                            }}
                        />,
                        <Select
                            maxTagCount='responsive'
                            key='contractor'
                            placeholder='Поставщик'
                            value={filter?.contractorIds}
                            size='middle'
                            mode='multiple'
                            allowClear={true}
                            style={{ width: 200 }}
                            showSearch
                            onChange={(value) => d(setFilter({ ...filter, contractorIds: value }, filterContext))}
                            optionFilterProp='children'
                            filterOption={(input, option) => (option?.label as string).toLowerCase().includes(input.toLowerCase())}
                            filterSort={(a, b) => (a?.label as string).toLowerCase().localeCompare((b?.label as string).toLowerCase())}
                            options={contractors.map((c) => {
                                return { value: c.id, label: c.login };
                            })}
                        />,
                        <Select
                            key='country'
                            placeholder='Страна'
                            mode='multiple'
                            maxTagCount='responsive'
                            value={filter?.countryIds}
                            size='middle'
                            allowClear={true}
                            style={{ width: 200 }}
                            showSearch
                            onChange={(value) => d(setFilter({ ...filter, countryIds: value }, filterContext))}
                            optionFilterProp='children'
                            filterOption={(input, option) => (option?.label as string).toLowerCase().includes(input.toLowerCase())}
                            filterSort={(a, b) => (a?.label as string).toLowerCase().localeCompare((b?.label as string).toLowerCase())}
                            options={countries.map((c) => {
                                return { value: c.id, label: c.name };
                            })}
                        />,
                        <Select
                            key='item'
                            placeholder='Продукт'
                            mode='multiple'
                            maxTagCount='responsive'
                            value={filter?.itemIds}
                            size='middle'
                            allowClear={true}
                            style={{ width: 200 }}
                            showSearch
                            onChange={(value) => d(setFilter({ ...filter, itemIds: value }, filterContext))}
                            optionFilterProp='children'
                            filterOption={(input, option) => (option?.label as string).toLowerCase().includes(input.toLowerCase())}
                            filterSort={(a, b) => (a?.label as string).toLowerCase().localeCompare((b?.label as string).toLowerCase())}
                            options={items.map((c) => {
                                return { value: c.id, label: c.name };
                            })}
                        />,
                        <Select
                            key='cargo'
                            placeholder='Авиаперевозчик'
                            mode='multiple'
                            maxTagCount='responsive'
                            value={filter?.cargoCarrierIds}
                            size='middle'
                            allowClear={true}
                            style={{ width: 200 }}
                            showSearch
                            onChange={(value) => d(setFilter({ ...filter, cargoCarrierIds: value }, filterContext))}
                            optionFilterProp='children'
                            filterOption={(input, option) => (option?.label as string).toLowerCase().includes(input.toLowerCase())}
                            filterSort={(a, b) => (a?.label as string).toLowerCase().localeCompare((b?.label as string).toLowerCase())}
                            options={airCargoCarriers.map((c) => {
                                return { value: c.id, label: c.name };
                            })}
                        />,
                        <Input
                            style={{ width: 130 }}
                            key='awbNumber'
                            placeholder='AWB'
                            value={filter?.awbNumber}
                            onChange={(data) => {
                                d(setFilter({ ...filter, awbNumber: data.target.value }, filterContext));
                            }}
                        />,
                        <Select
                            style={{ width: 240 }}
                            key='truck'
                            placeholder='Машина'
                            value={filter?.truckIds}
                            allowClear={true}
                            size='middle'
                            mode='multiple'
                            maxTagCount='responsive'
                            showSearch
                            onChange={(value: any) => d(setFilter({ ...filter, truckIds: value }, filterContext))}
                            filterOption={(input: any, option: any) =>
                                (option?.label as string).toLowerCase().startsWith(input.toLowerCase())
                            }
                            options={trucks.map((t) => {
                                return { value: t.id, label: `${getDate(t.departureOn)} / ${t.number}` };
                            })}
                        />,
                        <Select
                            key='city'
                            placeholder='Город доставки'
                            value={filter?.cityIds}
                            size='middle'
                            mode='multiple'
                            maxTagCount='responsive'
                            allowClear
                            style={{ width: 200 }}
                            onChange={(value) => d(setFilter({ ...filter, cityIds: value }, filterContext))}
                            filterOption={(input, option) => (option?.label as string).toLowerCase().includes(input.toLowerCase())}
                            filterSort={(a, b) => (a?.label as string).toLowerCase().localeCompare((b?.label as string).toLowerCase())}
                            options={cities.map((s) => {
                                return { value: s.id, label: s.name };
                            })}
                        />,
                        <RangePicker
                            style={{ width: 220 }}
                            allowEmpty={[true, true]}
                            key='loading'
                            format='DD.MM.YYYY'
                            placeholder={['Дата с', 'до']}
                            cellRender={(current) => {
                                return <div className='ant-picker-cell-inner'>{(current as Dayjs).date()}</div>;
                            }}
                            value={[filter?.dateFrom ? dayjs(filter?.dateFrom) : null, filter?.dateTo ? dayjs(filter?.dateTo) : null]}
                            onChange={(value) => {
                                if (!value) {
                                    d(
                                        setFilter(
                                            {
                                                ...filter,
                                                dateFrom: undefined,
                                                dateTo: undefined,
                                            },
                                            filterContext
                                        )
                                    );

                                    return;
                                }

                                d(
                                    setFilter(
                                        {
                                            ...filter,
                                            dateFrom: value[0]
                                                ? dayjs(value[0]).utc(true).set('hour', 0).set('minute', 0).set('second', 0).toString()
                                                : undefined,
                                            dateTo: value[1]
                                                ? dayjs(value[1]).utc(true).set('hour', 0).set('minute', 0).set('second', 0).toString()
                                                : undefined,
                                        },
                                        filterContext
                                    )
                                );
                            }}
                        />,
                        <Form.Item
                            key='withFlightOnly'
                            label={
                                <Tooltip title='Только с перелетом'>
                                    <Flex align='center'>
                                        <AirplaneIcon style={{ color: '#000000' }} />
                                    </Flex>
                                </Tooltip>
                            }
                            style={{ margin: 0 }}
                        >
                            <Switch
                                checked={filter?.withFlightOnly}
                                onChange={(value: any) => {
                                    d(setFilter({ ...filter, withFlightOnly: value }, filterContext));
                                }}
                            />
                        </Form.Item>,
                        <Form.Item key='isChanged' label='Измененные' style={{ margin: 0 }}>
                            <Switch
                                checked={filter?.isChanged}
                                onChange={(value: boolean) => {
                                    d(setFilter({ ...filter, isChanged: value }, filterContext));
                                }}
                            />
                        </Form.Item>,
                    ]}
                    onClear={() => d(setFilter(initFilter, filterContext))}
                />
            </div>
        );
    };

    const renderToolbar = () => {
        return (
            <Toolbar
                commands={[
                    {
                        label: 'Обновить',
                        key: 'refresh',
                        disabled: loading,
                        icon: <ReloadOutlined />,
                        onClick: () => {
                            setRefreshRequired(true);
                        },
                    },
                    {
                        label: 'Выгрузить в Excel',
                        key: 'exportToExcel',
                        icon: <FileExcelOutlined />,
                        onClick: () => onExport(),
                    },
                ]}
                filter={{
                    active: showFilter,
                    onHideFilter: () => {
                        setShowFilter(!showFilter);
                    },
                }}
            />
        );
    };

    const expandedBills = (record: IBillHeader) => {
        const columns: ColumnsType<IBill> = [
            {
                title: '#',
                align: 'center',
                width: 100,
                render: (_, record) => {
                    return (
                        <Flex vertical gap='small' align='center' justify='center'>
                            <Button
                                variant='link'
                                color='blue'
                                style={{ color: '#1677ff', fontWeight: 600 }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setBills([record]);
                                }}
                            >
                                {record.fullNumber}
                            </Button>

                            <Flex justify='center' align='center'>
                                {record.boxGroupId && (
                                    <Tooltip title='Детали заказа'>
                                        <Button
                                            size='large'
                                            variant='link'
                                            color='blue'
                                            target='_blank'
                                            href={`/orders/${currentBill?.userId}/${currentBill?.boxGroupId}`}
                                            icon={<UnorderedListOutlined />}
                                            onClick={(e) => e.stopPropagation()}
                                        />
                                    </Tooltip>
                                )}

                                {record.comment && <Comment comment={record.comment} />}
                            </Flex>

                            <Flex gap='small' align='center' justify='center'>
                                <Tooltip
                                    placement='top'
                                    title={
                                        record.status == BillStatus.Completed ||
                                        record.status === BillStatus.Paid ||
                                        record.status == BillStatus.Abandoned
                                            ? 'Расчет Проведен'
                                            : 'Счет ожидает проверки'
                                    }
                                    color={
                                        record.status == BillStatus.Completed ||
                                        record.status === BillStatus.Paid ||
                                        record.status == BillStatus.Abandoned
                                            ? 'var(--main-green)'
                                            : 'darkorange'
                                    }
                                >
                                    <>
                                        {(record.status == BillStatus.Completed ||
                                            record.status === BillStatus.Paid ||
                                            record.status == BillStatus.Abandoned) && (
                                            <CheckCircleOutlined
                                                style={{
                                                    color: 'var(--main-green)',
                                                    fontSize: 18,
                                                }}
                                            />
                                        )}

                                        {record.status == BillStatus.ToVerify && (
                                            <Tooltip placement='top' title='Счет ожидает проверки' color='#f6546a'>
                                                <WarningOutlined
                                                    style={{
                                                        color: '#f6546a',
                                                        fontSize: 18,
                                                    }}
                                                />
                                            </Tooltip>
                                        )}
                                    </>
                                </Tooltip>

                                {record.isChanged && (
                                    <Tooltip title={`Счет изменен ${getDate(record.changedOn)}`}>
                                        <div>
                                            <PenIcon style={{ color: 'var(--main-green)' }} />
                                        </div>
                                    </Tooltip>
                                )}
                            </Flex>

                            {record.tag && (
                                <Tag color='#000000' style={{ marginRight: 0 }}>
                                    #{record.tag}
                                </Tag>
                            )}
                        </Flex>
                    );
                },
            },
            {
                title: '',
                width: 400,
                onCell: (record, index) => ({
                    colSpan: record.boxGroupId ? 1 : 5,
                }),
                render: (_: any, record) => {
                    if (!record.boxGroupId) {
                        return record.comment;
                    }

                    return (
                        <Form colon={false} labelCol={{ xs: 8 }} wrapperCol={{ xs: 16 }}>
                            <Form.Item label='ID' style={{ marginBottom: 0 }}>
                                <span style={{ fontWeight: 600 }}>{record.consigneeCode}</span>
                            </Form.Item>
                            <Form.Item label='Маркировка' style={{ marginBottom: 0 }}>
                                <span style={{ fontWeight: 600 }}>{record.markingCode}</span>
                            </Form.Item>
                            <Form.Item label='Контрагент' style={{ marginBottom: 0 }}>
                                <span style={{ fontWeight: 600 }}>{record.legalName}</span>
                            </Form.Item>
                            <Form.Item label='Расчетный город' style={{ marginBottom: 0 }}>
                                <span style={{ fontWeight: 600 }}>{record.cityName}</span>
                            </Form.Item>
                            {record.contractorName && (
                                <Form.Item label='Поставщик' style={{ marginBottom: 0 }}>
                                    <span style={{ fontWeight: 600 }}>{record.contractorName}</span>
                                </Form.Item>
                            )}
                        </Form>
                    );
                },
            },
            {
                align: 'center',
                width: 375,
                onCell: (record, index) => ({
                    colSpan: record.boxGroupId ? 1 : 0,
                }),
                render: (_, record) => {
                    let diffQty: number = 0;
                    let useAdjustmentQty: boolean = false;

                    if (record.qty && record.unitType != UnitType.Weight) {
                        if (record.packages && record.packages.length > 0) {
                            let actualQty: number = 0;

                            if (record.packages) {
                                record.packages.map((p) => {
                                    actualQty += p.boxQty || 0;
                                });
                            }

                            diffQty = actualQty - record.qty;
                        } else {
                            useAdjustmentQty = true;
                        }
                    }

                    const country = countries.find((c) => c.id == record.countryId);
                    if (!country) return;

                    return (
                        <ConsignmentCard
                            country={country}
                            api={api}
                            items={[
                                {
                                    itemName: record.itemName,
                                    itemColor: record.itemColor,
                                    qty: record.qty,
                                    diffQty: diffQty,
                                    useAdjustmentQty: useAdjustmentQty,
                                    adjustmentQty: record.adjustmentQty,
                                    loadingOn: record.billDate,
                                    awbNumber: record.awbNumber,
                                    cargoCarrierName: record.cargoCarrierName,
                                    truckNumber: record.truckNumber,
                                    comment: record.boxGroupComment,
                                },
                            ]}
                            serverFetch={serverFetch}
                        />
                    );
                },
            },
            {
                title: 'Вес',
                width: 180,
                align: 'center',
                onCell: (record, index) => ({
                    colSpan: record.boxGroupId ? 1 : 0,
                }),
                render: (_: any, record) => {
                    if (record.unitType !== UnitType.Weight) return '';

                    const setting = tariffSettings.find((s) => s.consigneeId == record?.consigneeId && s.countryId == record?.countryId);

                    return (
                        <Form colon={false} labelCol={{ xs: 12 }} wrapperCol={{ xs: 12 }}>
                            <Form.Item label='Брутто' style={{ marginBottom: 0 }}>
                                {record.grossWeight && (
                                    <Flex justify='start' style={{ fontWeight: 600 }}>
                                        {record.grossWeight} <span>кг</span>
                                        {setting && setting.weightType == WeightType.Gross && (
                                            <Tooltip title='Расчетный вес'>
                                                <CheckCircleFilled style={{ color: '#FFDD2D', marginLeft: 5 }} />
                                            </Tooltip>
                                        )}
                                    </Flex>
                                )}
                            </Form.Item>
                            <Form.Item label='Объемный'>
                                {record.volumeWeight && (
                                    <Flex justify='start' style={{ fontWeight: 600 }}>
                                        {record.volumeWeight} <span>кг</span>
                                        {(!setting || setting.weightType == WeightType.Volume) && (
                                            <Tooltip title='Расчетный вес'>
                                                <CheckCircleFilled style={{ color: '#FFDD2D', marginLeft: 5 }} />
                                            </Tooltip>
                                        )}
                                    </Flex>
                                )}
                            </Form.Item>
                        </Form>
                    );
                },
            },
            {
                title: 'Тариф',
                width: 100,
                align: 'center',
                onCell: (record, index) => ({
                    colSpan: record.boxGroupId ? 1 : 0,
                }),
                render: (_: any, record) => {
                    if (!record.boxGroupId) return;

                    return (
                        <TariffCard
                            cargoCarrierId={record.cargoCarrierId}
                            flightPrice={record.flightPrice}
                            truckPrice={record.truckPrice}
                        />
                    );
                },
            },
            {
                title: 'Упаковка',
                width: 280,
                onCell: (record, index) => ({
                    colSpan: record.boxGroupId ? 1 : 0,
                }),
                render: (_: any, record) => {
                    if (record.unitType == UnitType.Cart) {
                        return (
                            <Tag color='#780606' style={{ fontSize: 14, marginLeft: 5, marginTop: 5, marginBottom: 5 }}>
                                Телега
                            </Tag>
                        );
                    } else if (record.unitType == UnitType.Pallet) {
                        if (record.packages && record.packages.length > 0) {
                            return record.packages?.map((p) => {
                                return (
                                    <Tag
                                        key={p.templateId}
                                        color='green'
                                        style={{ fontSize: 14, marginLeft: 5, marginTop: 5, marginBottom: 5, color: '#000000' }}
                                    >
                                        <span style={{ fontWeight: 600 }}>{p.templateName}</span> : {p.boxQty}
                                    </Tag>
                                );
                            });
                        } else {
                            return (
                                <Tag color='#009a66' style={{ fontSize: 14, marginLeft: 5, marginTop: 5, marginBottom: 5 }}>
                                    Паллета
                                </Tag>
                            );
                        }
                    }
                },
            },
            {
                title: `Итого (${currencySign(Currency.Usd)})`,
                width: 140,
                align: 'center',
                onCell: (record) => ({
                    style: {
                        background: '#FFEE96',
                        fontWeight: 600,
                    },
                }),
                render: (_: any, record) => {
                    return (
                        <Flex vertical gap='small' justify='center' style={{ color: record.totalPrice <= 0 ? 'red' : '' }}>
                            <Flex gap='small' justify='center' style={{ color: record.totalPrice <= 0 ? 'red' : '' }}>
                                {record.totalPrice && toFinanceString(record.totalPrice, 2)}
                                {record.isTotalPriceManual && (
                                    <Tooltip title='Счет изменен вручную'>
                                        <FormOutlined />
                                    </Tooltip>
                                )}
                            </Flex>

                            {record.discount && (
                                <Tooltip title='Cкидка' placement='left'>
                                    <div style={{ color: 'var(--main-green)', padding: 3, marginTop: 5 }}>
                                        {toFinanceString(-1 * record.discount, 2)}
                                    </div>
                                </Tooltip>
                            )}

                            {record.totalPriceFinal > 0 && record.totalPriceFinal != record.totalPrice && (
                                <Tooltip title='Со скидкой' placement='left'>
                                    <div style={{ background: '#d2dcd8', padding: 3, marginTop: 5 }}>
                                        {toFinanceString(record.totalPriceFinal, 2)}
                                    </div>
                                </Tooltip>
                            )}

                            <div style={{ padding: 3, marginTop: 5 }}>
                                {record.status == BillStatus.Paid && (
                                    <Tag style={{ marginRight: 0 }} color='var(--main-green)'>
                                        ОПЛАЧЕН
                                    </Tag>
                                )}
                            </div>
                        </Flex>
                    );
                },
            },
            {
                title: `Итого (${currencySign(Currency.Rub)})`,
                width: 140,
                align: 'center',
                onCell: (record) => ({
                    style: {
                        fontWeight: 600,
                    },
                }),
                render: (_: any, record) => {
                    return (
                        <div style={{ color: record.totalPriceRub <= 0 ? 'red' : '' }}>
                            {record.totalPriceRub && toFinanceString(record.totalPriceRub, 2)}
                            {record.totalPriceFinalRub > 0 && record.totalPriceFinalRub != record.totalPriceRub && (
                                <Tooltip title='Со скидкой'>
                                    <div style={{ background: '#d2dcd8', padding: 3, marginTop: 5 }}>
                                        {toFinanceString(record.totalPriceFinalRub, 2)}
                                    </div>
                                </Tooltip>
                            )}
                        </div>
                    );
                },
            },
            {},
        ];

        return (
            <Table
                rowKey='id'
                size='small'
                columns={columns}
                dataSource={record.bills}
                pagination={false}
                rowSelection={{
                    selectedRowKeys: selectedIds,
                    onChange: onSelectChange,
                    type: 'checkbox',
                    columnWidth: 32,
                    getCheckboxProps: (record) => ({
                        style: { display: record.status == BillStatus.Abandoned ? 'none' : '' },
                    }),
                }}
                onRow={(record) => {
                    return {
                        onClick: (event) => {
                            onSelectChange([], [record]);
                        },
                    };
                }}
            />
        );
    };

    const renderTable = () => {
        const columns: ColumnsType<IBillHeader> = [
            {
                title: 'Отгрузка',
                width: 130,
                align: 'center',
                render: (_, record) => {
                    return record.billDate && getDate(record.billDate);
                },
            },
            {
                align: 'center',
                width: 40,
                render: (_, record) => {
                    return <Button type='text' icon={<EyeFilled />} onClick={() => setBills(record.bills)} />;
                },
            },
            {
                title: `Общий Счет (${currencySign(Currency.Usd)})`,
                align: 'left',
                width: 300,
                onCell: (record) => ({
                    style: {
                        background: '#FFEE96',
                        fontWeight: 600,
                    },
                }),
                render: (_, record) => {
                    return (
                        <Flex align='center' gap='large'>
                            {record.totalPrice && toFinanceString(record.totalPrice, 2)}
                            {record.totalPriceFinal > 0 && record.totalPriceFinal != record.totalPrice && (
                                <Tooltip title='Со скидкой'>
                                    <div style={{ background: '#d2dcd8', padding: '3px 6px' }}>
                                        {toFinanceString(record.totalPriceFinal, 2)}
                                    </div>
                                </Tooltip>
                            )}
                        </Flex>
                    );
                },
            },
            {},
        ];

        return (
            <Table
                rowKey='billDate'
                size='small'
                loading={{
                    spinning: loading,
                    indicator: <LoadingOutlined style={{ fontSize: 44 }} spin />,
                }}
                columns={columns}
                expandable={{
                    columnWidth: 32,
                    expandedRowRender: expandedBills,
                }}
                dataSource={headers}
                pagination={false}
                virtual={true}
                scroll={{ y: height ?? 0 + (showFilter ? 0 : 58), x: 'max-content' }}
            />
        );
    };

    return (
        <div ref={containerRef} style={{ height: `calc(100vh - 195px - ${filterHeight}px - ${userId ? 68 : 0}px)` }}>
            {userId && <FormHeader title={`Счета "${user?.login}"`} />}

            {renderToolbar()}
            {renderFilter()}
            {renderTable()}

            {openBill && (
                <Bill
                    bills={bills}
                    onClose={() => {
                        setBills([]);
                        setOpenBill(false);
                    }}
                />
            )}

            {notificationContextHolder}
        </div>
    );
};

export default BillHeaders;
